import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { 
  ArrowLeft,
  ChartLine,
  DeviceMobile,
  Desktop,
  DeviceTablet,
  Globe,
  Calendar,
  AppleLogo,
  AndroidLogo,
  WindowsLogo,
  LinuxLogo,
  Desktop as DesktopIcon
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/App";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie
} from 'recharts';

const OS_COLORS = {
  iOS: '#008AD8',
  Android: '#057EF3',
  Windows: '#003594',
  macOS: '#003399',
  Linux: '#2A4A6C',
  other: '#1A3A5C',
};

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { qrId } = useParams();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project");
  
  const [analytics, setAnalytics] = useState(null);
  const [qrcode, setQrcode] = useState(null);
  const [project, setProject] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const isQRView = Boolean(qrId);
  const isProjectView = Boolean(projectId);

  useEffect(() => {
    fetchAnalytics();
  }, [qrId, projectId]);

  const fetchAnalytics = async () => {
    try {
      if (isQRView) {
        const [analyticsRes, qrcodeRes] = await Promise.all([
          api.get(`/analytics/qrcode/${qrId}?days=30`),
          api.get(`/qrcodes/${qrId}`)
        ]);
        setAnalytics(analyticsRes.data);
        setQrcode(qrcodeRes.data);
      } else if (isProjectView) {
        const [analyticsRes, projectRes] = await Promise.all([
          api.get(`/analytics/project/${projectId}?days=30`),
          api.get(`/projects/${projectId}`)
        ]);
        setAnalytics(analyticsRes.data);
        setProject(projectRes.data);
      } else {
        const overviewRes = await api.get("/analytics/overview");
        setOverview(overviewRes.data);
      }
    } catch (error) {
      toast.error("Error al cargar analiticas");
    } finally {
      setLoading(false);
    }
  };

  const getDeviceName = (device) => {
    switch (device?.toLowerCase()) {
      case 'mobile': return 'Movil';
      case 'tablet': return 'Tablet';
      case 'desktop': return 'Escritorio';
      default: return 'Desconocido';
    }
  };

  const getOSIcon = (os) => {
    switch (os) {
      case 'iOS': return AppleLogo;
      case 'Android': return AndroidLogo;
      case 'Windows': return WindowsLogo;
      case 'macOS': return AppleLogo;
      case 'Linux': return LinuxLogo;
      default: return DesktopIcon;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#05152E' }}>
        <div className="w-12 h-12 border-4 border-t-transparent animate-spin" style={{ borderColor: '#008AD8', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  // Overview Page (no specific QR or project)
  if (!isQRView && !isProjectView) {
    return (
      <div className="min-h-screen dark-dashboard" style={{ backgroundColor: '#05152E' }} data-testid="analytics-overview">
        <header className="border-b sticky top-0 z-50" style={{ backgroundColor: '#05152E', borderColor: '#1A3A5C' }}>
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <button
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="p-2 hover:bg-[#0D2847] transition-colors"
              style={{ color: '#A1A1AA' }}
            >
              <ArrowLeft size={24} weight="bold" />
            </button>
            <div>
              <p className="uppercase text-[10px] tracking-[0.1em] font-medium" style={{ color: '#71717A' }}>Resumen</p>
              <h1 className="text-xl font-black text-white">
                ANALITICAS
              </h1>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border" style={{ borderColor: '#1A3A5C' }}>
            {[
              { label: "Total Codigos QR", value: overview?.total_qrcodes || 0 },
              { label: "Total Proyectos", value: overview?.total_projects || 0 },
              { label: "Total Escaneos", value: overview?.total_scans || 0, accent: true },
              { label: "Ultimos 7 Dias", value: overview?.recent_scans || 0 },
            ].map((stat, index) => (
              <div 
                key={index} 
                className={`p-6 ${index < 3 ? 'border-r' : ''} ${index < 2 ? 'border-b lg:border-b-0' : ''}`}
                style={{ backgroundColor: '#0A1F3D', borderColor: '#1A3A5C' }}
                data-testid={`overview-stat-${index}`}
              >
                <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>{stat.label}</p>
                <p 
                  className="text-3xl font-bold"
                  style={{ fontFamily: 'IBM Plex Mono, monospace', color: stat.accent ? '#008AD8' : 'white' }}
                >
                  {stat.value.toLocaleString()}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 p-12 border text-center" style={{ borderColor: '#1A3A5C', backgroundColor: '#0A1F3D' }}>
            <ChartLine size={48} weight="duotone" className="mx-auto mb-4" style={{ color: '#1A3A5C' }} />
            <h3 className="font-bold text-lg text-white mb-2">
              Selecciona un Codigo QR o Proyecto
            </h3>
            <p className="mb-4" style={{ color: '#71717A' }}>
              Ve analiticas detalladas seleccionando un codigo QR o proyecto especifico desde el panel
            </p>
            <button
              data-testid="go-to-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="px-6 py-3 bg-transparent text-white font-semibold border transition-colors hover:border-[#008AD8] hover:text-[#008AD8]"
              style={{ borderColor: '#1A3A5C' }}
            >
              Ir al Panel
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Detailed Analytics View
  return (
    <div className="min-h-screen dark-dashboard" style={{ backgroundColor: '#05152E' }} data-testid="analytics-detail">
      <header className="border-b sticky top-0 z-50" style={{ backgroundColor: '#05152E', borderColor: '#1A3A5C' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            data-testid="analytics-back-btn"
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-[#0D2847] transition-colors"
            style={{ color: '#A1A1AA' }}
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div>
            <p className="uppercase text-[10px] tracking-[0.1em] font-medium" style={{ color: '#71717A' }}>
              Analiticas de {isQRView ? 'Codigo QR' : 'Proyecto'}
            </p>
            <h1 className="text-xl font-black text-white">
              {isQRView ? `/${qrcode?.slug}` : project?.name}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border mb-8" style={{ borderColor: '#1A3A5C' }}>
          <div className="p-6 border-b lg:border-b-0 lg:border-r" style={{ backgroundColor: '#0A1F3D', borderColor: '#1A3A5C' }}>
            <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>Total Escaneos</p>
            <p 
              className="text-4xl font-bold"
              style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#008AD8' }}
              data-testid="total-scans-value"
            >
              {analytics?.total_scans?.toLocaleString() || 0}
            </p>
          </div>
          <div className="p-6 border-b lg:border-b-0 lg:border-r" style={{ backgroundColor: '#0A1F3D', borderColor: '#1A3A5C' }}>
            <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>Periodo</p>
            <p 
              className="text-4xl font-bold text-white"
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              30 Dias
            </p>
          </div>
          <div className="p-6" style={{ backgroundColor: '#0A1F3D' }}>
            <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>
              {isQRView ? 'Destino' : 'Codigos QR'}
            </p>
            {isQRView ? (
              <p 
                className="text-sm truncate"
                style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#A1A1AA' }}
              >
                {qrcode?.destination_url}
              </p>
            ) : (
              <p 
                className="text-4xl font-bold text-white"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              >
                {analytics?.qr_count || 0}
              </p>
            )}
          </div>
        </div>

        {/* Scans by Day Chart */}
        <div className="border p-6 mb-8" style={{ borderColor: '#1A3A5C', backgroundColor: '#0A1F3D' }}>
          <div className="flex items-center gap-2 mb-6">
            <Calendar size={20} weight="bold" style={{ color: '#71717A' }} />
            <h3 className="font-bold text-white">
              ESCANEOS POR DIA
            </h3>
          </div>
          
          {analytics?.scans_by_day?.length > 0 ? (
            <div className="h-64" data-testid="scans-by-day-chart" style={{ minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                <LineChart data={analytics.scans_by_day}>
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono', fill: '#71717A' }}
                    tickFormatter={(value) => value.slice(5)}
                    stroke="#1A3A5C"
                    axisLine={{ stroke: '#1A3A5C' }}
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono', fill: '#71717A' }}
                    stroke="#1A3A5C"
                    axisLine={{ stroke: '#1A3A5C' }}
                    tickLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0A1F3D',
                      border: '1px solid #1A3A5C',
                      borderRadius: 0,
                      color: '#FFFFFF',
                      fontFamily: 'IBM Plex Mono'
                    }}
                    labelStyle={{ color: '#71717A' }}
                    formatter={(value) => [value, 'Escaneos']}
                  />
                  <Line 
                    type="stepAfter" 
                    dataKey="count" 
                    stroke="#008AD8" 
                    strokeWidth={2}
                    dot={{ fill: '#008AD8', strokeWidth: 0, r: 4 }}
                    activeDot={{ fill: '#FFFFFF', r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center" style={{ color: '#71717A' }}>
              No hay datos de escaneo disponibles
            </div>
          )}
        </div>

        {/* OS Breakdown - iOS vs Android */}
        <div className="border p-6 mb-8" style={{ borderColor: '#1A3A5C', backgroundColor: '#0A1F3D' }} data-testid="os-breakdown-section">
          <div className="flex items-center gap-2 mb-6">
            <DeviceMobile size={20} weight="bold" style={{ color: '#71717A' }} />
            <h3 className="font-bold text-white">
              SISTEMA OPERATIVO
            </h3>
          </div>
          
          {analytics?.scans_by_os?.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* OS Bar breakdown */}
              <div className="space-y-4">
                {analytics.scans_by_os.map((item) => {
                  const Icon = getOSIcon(item.os);
                  const total = analytics.scans_by_os.reduce((acc, i) => acc + i.count, 0);
                  const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  const color = OS_COLORS[item.os] || OS_COLORS.other;
                  
                  return (
                    <div key={item.os} className="flex items-center gap-4" data-testid={`os-item-${item.os}`}>
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0D2847' }}>
                        <Icon size={22} weight="bold" style={{ color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-white text-sm">{item.os || 'Desconocido'}</span>
                          <span 
                            className="text-sm"
                            style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#A1A1AA' }}
                          >
                            {item.count} ({percentage}%)
                          </span>
                        </div>
                        <div className="h-2" style={{ backgroundColor: '#0D2847' }}>
                          <div 
                            className="h-full transition-all duration-500"
                            style={{ width: `${percentage}%`, backgroundColor: color }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* OS Pie chart */}
              <div className="h-48 flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  <PieChart>
                    <Pie
                      data={analytics.scans_by_os.map(item => ({
                        name: item.os || 'Otro',
                        value: item.count
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                      stroke="none"
                    >
                      {analytics.scans_by_os.map((item, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={OS_COLORS[item.os] || OS_COLORS.other}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ 
                        backgroundColor: '#0A1F3D',
                        border: '1px solid #1A3A5C',
                        borderRadius: 0,
                        color: '#FFFFFF',
                        fontFamily: 'IBM Plex Mono'
                      }}
                      formatter={(value, name) => [value, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center" style={{ color: '#71717A' }}>
              No hay datos de sistema operativo disponibles
            </div>
          )}
        </div>

        {/* Device & Country Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border" style={{ borderColor: '#1A3A5C' }}>
          {/* Scans by Device */}
          <div className="border-b lg:border-b-0 lg:border-r p-6" style={{ borderColor: '#1A3A5C', backgroundColor: '#0A1F3D' }} data-testid="device-breakdown-section">
            <div className="flex items-center gap-2 mb-6">
              <DeviceTablet size={20} weight="bold" style={{ color: '#71717A' }} />
              <h3 className="font-bold text-white">
                POR DISPOSITIVO
              </h3>
            </div>
            
            {analytics?.scans_by_device?.length > 0 ? (
              <div className="space-y-4">
                {analytics.scans_by_device.map((item) => {
                  const total = analytics.scans_by_device.reduce((acc, i) => acc + i.count, 0);
                  const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  
                  return (
                    <div key={item.device} className="flex items-center gap-4" data-testid={`device-item-${item.device}`}>
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0D2847' }}>
                        {item.device === 'mobile' ? <DeviceMobile size={22} weight="bold" style={{ color: '#A1A1AA' }} /> :
                         item.device === 'tablet' ? <DeviceTablet size={22} weight="bold" style={{ color: '#A1A1AA' }} /> :
                         <Desktop size={22} weight="bold" style={{ color: '#A1A1AA' }} />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-white text-sm">{getDeviceName(item.device)}</span>
                          <span 
                            className="text-sm"
                            style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#A1A1AA' }}
                          >
                            {item.count} ({percentage}%)
                          </span>
                        </div>
                        <div className="h-2" style={{ backgroundColor: '#0D2847' }}>
                          <div 
                            className="h-full transition-all duration-500"
                            style={{ width: `${percentage}%`, backgroundColor: '#008AD8' }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center" style={{ color: '#71717A' }}>
                No hay datos de dispositivo disponibles
              </div>
            )}
          </div>

          {/* Scans by Country */}
          <div className="p-6" style={{ backgroundColor: '#0A1F3D' }} data-testid="country-breakdown-section">
            <div className="flex items-center gap-2 mb-6">
              <Globe size={20} weight="bold" style={{ color: '#71717A' }} />
              <h3 className="font-bold text-white">
                POR PAIS
              </h3>
            </div>
            
            {analytics?.scans_by_country?.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  <BarChart 
                    data={analytics.scans_by_country.slice(0, 5)} 
                    layout="vertical"
                  >
                    <XAxis 
                      type="number"
                      tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono', fill: '#71717A' }}
                      stroke="#1A3A5C"
                      axisLine={{ stroke: '#1A3A5C' }}
                      tickLine={false}
                    />
                    <YAxis 
                      type="category"
                      dataKey="country"
                      tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono', fill: '#A1A1AA' }}
                      stroke="#1A3A5C"
                      axisLine={{ stroke: '#1A3A5C' }}
                      tickLine={false}
                      width={60}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0A1F3D',
                        border: '1px solid #1A3A5C',
                        borderRadius: 0,
                        color: '#FFFFFF',
                        fontFamily: 'IBM Plex Mono'
                      }}
                      formatter={(value) => [value, 'Escaneos']}
                    />
                    <Bar dataKey="count" fill="#008AD8">
                      {analytics.scans_by_country.slice(0, 5).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={index === 0 ? '#008AD8' : '#1A3A5C'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center" style={{ color: '#71717A' }}>
                No hay datos de pais disponibles
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
